// Analytics helper for GA4 (Direct gtag implementation)
// Measurement ID: G-0M9LEE5SCJ

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
  }
}

// Track events via GA4 gtag only (no dataLayer to prevent future GTM conflicts)
export const trackEvent = (
  eventName: string,
  eventParams?: Record<string, unknown>
) => {
  if (typeof window === 'undefined' || !window.gtag) return;
  
  // Filter out any PII fields that might accidentally be passed
  const safeParams = eventParams ? filterPII(eventParams) : {};
  
  window.gtag('event', eventName, {
    event_category: safeParams.event_category || 'engagement',
    event_label: safeParams.event_label || eventName,
    ...safeParams,
  });
};

// Filter out PII from event parameters
function filterPII(params: Record<string, unknown>): Record<string, unknown> {
  const piiFields = ['name', 'email', 'phone', 'mobile', 'address', 'full_name', 'user_name'];
  const filtered: Record<string, unknown> = {};
  
  for (const [key, value] of Object.entries(params)) {
    if (!piiFields.includes(key.toLowerCase())) {
      filtered[key] = value;
    }
  }
  
  return filtered;
}

// Track page view for SPA navigation
export const trackPageView = (pagePath: string, pageTitle?: string) => {
  if (typeof window === 'undefined' || !window.gtag) return;
  
  window.gtag('event', 'page_view', {
    page_path: pagePath,
    page_title: pageTitle || document.title,
  });
};

// Specific event tracking functions
export const analytics = {
  // Generic event tracking
  trackEvent: (eventName: string, params?: Record<string, unknown>) => {
    trackEvent(eventName, params);
  },

  // Lead form events
  leadOpenModal: (source: string, pagePath: string) => {
    trackEvent('lead_open_modal', {
      lead_source: source,
      page_path: pagePath,
    });
  },

  leadSubmit: (source: string, pagePath: string, metadata?: { buyerType?: string; interestType?: string; siteVisitPreference?: string }) => {
    trackEvent('lead_submit', {
      event_category: 'lead',
      event_label: `Lead Form - ${source}`,
      lead_source: source,
      page_path: pagePath,
      buyer_type: metadata?.buyerType || 'not_specified',
      interest_type: metadata?.interestType || 'not_specified',
      site_visit_preference: metadata?.siteVisitPreference || 'not_specified',
      conversion: true,
    });
  },

  leadFormError: (source: string, errorType: string) => {
    trackEvent('lead_form_error', {
      lead_source: source,
      error_type: errorType,
    });
  },

  // CTA click events
  whatsappClick: (pagePath: string) => {
    trackEvent('whatsapp_click', {
      event_category: 'engagement',
      event_label: 'WhatsApp Enquiry',
      page_path: pagePath,
      cta_type: 'whatsapp',
    });
  },

  callClick: (pagePath: string) => {
    trackEvent('click_call', {
      event_category: 'engagement',
      event_label: 'Phone Call',
      page_path: pagePath,
      cta_type: 'call',
    });
  },

  brochureDownload: (pagePath: string) => {
    trackEvent('brochure_download', {
      event_category: 'engagement',
      event_label: 'Project Brochure',
      page_path: pagePath,
    });
  },

  brochureDownloadIntent: (pagePath: string) => {
    trackEvent('brochure_download_intent', {
      page_path: pagePath,
    });
  },

  // Dossier events
  dossierDownloadIntent: (pagePath: string, source: string) => {
    trackEvent('dossier_download_intent', {
      page_path: pagePath,
      cta_source: source,
    });
  },

  dossierDownload: (pagePath: string, buyerPurpose?: string) => {
    trackEvent('dossier_download', {
      event_category: 'conversion',
      event_label: 'Project Dossier',
      page_path: pagePath,
      buyer_purpose: buyerPurpose || 'not_specified',
      conversion: true,
    });
  },

  // WhatsApp with context
  whatsappClickWithContext: (pagePath: string, ctaContext: string) => {
    trackEvent('whatsapp_click', {
      event_category: 'engagement',
      event_label: 'WhatsApp Enquiry',
      page_path: pagePath,
      cta_type: 'whatsapp',
      cta_context: ctaContext,
    });
  },

  priceSheetIntent: (pagePath: string) => {
    trackEvent('price_sheet_intent', {
      page_path: pagePath,
    });
  },

  floorPlanDownloadIntent: (planType: string, pagePath: string) => {
    trackEvent('floor_plan_download_intent', {
      plan_type: planType,
      page_path: pagePath,
    });
  },

  // Navigation events
  pageView: (pagePath: string, pageTitle: string) => {
    trackEvent('page_view', {
      page_path: pagePath,
      page_title: pageTitle,
    });
  },

  scrollDepth: (percentage: number, pagePath: string) => {
    trackEvent('scroll_depth', {
      scroll_percentage: percentage,
      page_path: pagePath,
    });
  },

  // Gallery events
  galleryView: (imageCategory: string) => {
    trackEvent('gallery_view', {
      image_category: imageCategory,
    });
  },

  virtualTourStart: () => {
    trackEvent('virtual_tour_start', {});
  },

  // Gold Assurance campaign events
  viewGoldAssurance: (pagePath: string) => {
    trackEvent('view_gold_assurance', {
      event_category: 'campaign',
      event_label: 'Gold Assurance Viewed',
      page_path: pagePath,
      campaign_name: 'makar_sankranti_gold_2026',
    });
  },

  // FAQ engagement
  faqExpand: (question: string, pagePath: string) => {
    trackEvent('faq_expand', {
      event_category: 'engagement',
      event_label: question.substring(0, 50),
      page_path: pagePath,
      faq_question: question.substring(0, 100),
    });
  },

  // Internal link click tracking
  internalLinkClick: (linkText: string, linkUrl: string, pagePath: string) => {
    trackEvent('internal_link_click', {
      event_category: 'navigation',
      event_label: linkText,
      page_path: pagePath,
      link_url: linkUrl,
    });
  },

  // Form error tracking (separate from lead_form_error)
  formError: (formName: string, errorField: string, errorMessage: string) => {
    trackEvent('form_error', {
      event_category: 'form',
      event_label: `${formName} - ${errorField}`,
      form_name: formName,
      error_field: errorField,
      error_message: errorMessage,
    });
  },

  // Form success (separate from lead_submit for non-lead forms)
  formSubmitSuccess: (formName: string, pagePath: string) => {
    trackEvent('form_submit_success', {
      event_category: 'conversion',
      event_label: formName,
      form_name: formName,
      page_path: pagePath,
      conversion: true,
    });
  },

  // Scroll 75% milestone (deep engagement signal)
  scroll75: (pagePath: string) => {
    trackEvent('scroll_75', {
      event_category: 'engagement',
      event_label: 'Deep Scroll',
      page_path: pagePath,
      scroll_milestone: 75,
    });
  },
};

// UTM capture utility
export const captureUTMParams = (): Record<string, string> => {
  if (typeof window === 'undefined') return {};

  const params = new URLSearchParams(window.location.search);
  const utmParams: Record<string, string> = {};

  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach(
    (param) => {
      const value = params.get(param);
      if (value) {
        utmParams[param] = value;
      }
    }
  );

  // Store in sessionStorage for persistence across pages
  if (Object.keys(utmParams).length > 0) {
    sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
  }

  return utmParams;
};

// Get stored UTM params
export const getStoredUTMParams = (): Record<string, string> => {
  if (typeof window === 'undefined') return {};

  const stored = sessionStorage.getItem('utm_params');
  return stored ? JSON.parse(stored) : {};
};
